<script setup lang="ts">
import { Sale, IconPercent, Pay } from '@xpos/icons-svg';
import { Close } from '@element-plus/icons-vue';
import { useI18n } from 'vue-i18n';
import { CalcType, IInvoice, IInvoiceDiscount } from '@posx/core';
import { Ref, inject, ref } from 'vue';
import { useInvoiceStore } from '@/stores/invoice';
import { formatCurrency } from '@/utils/formatNumber';

const { t } = useI18n();
const invoiceStore = useInvoiceStore();
const currentInvoice = inject<Ref<IInvoice | null>>('invoice', ref(null));

// Removes a discount from the invoice
const removeInvoiceDiscount = async (discount: IInvoiceDiscount) => {
  if (!currentInvoice?.value) return;
  const service = await invoiceStore.getService();
  service.removeInvoiceDiscount(currentInvoice.value, discount.uid);
  await invoiceStore.calculate(currentInvoice.value);
  if (currentInvoice.value) await invoiceStore.syncInvoice(currentInvoice.value);
};

// Removes balance usage from the invoice
const removeBalanceUsage = async () => {
  if (!currentInvoice?.value) return;
  currentInvoice.value.minus_credit = 0;
  await invoiceStore.calculate(currentInvoice.value);
};
</script>

<template>
  <div>
    <div class="text-sm">{{ t('discount_section.title') }}</div>
    <div>
      <el-row :gutter="12">
        <el-col v-for="(discount, index) in currentInvoice?.discounts" :span="12" class="mb-3">
          <x-item border class="w-full shrink-0 bg-white">
            <template #start>
              <div class="w-[60px] h-[40px] flex items-center justify-center text-primary rounded-lg">
                <el-icon size="24" v-if="discount.calc_type == CalcType.Flat">
                  <Sale />
                </el-icon>
                <el-icon size="24" v-if="discount.calc_type == CalcType.Percent">
                  <IconPercent />
                </el-icon>
              </div>
            </template>
            {{ discount.name === 'custom' ? discount.calc_type + ' discount' : discount.name }}
            <template #end>
              <div class="flex flex-wrap w-[100px] items-center justify-end">
                <span class="text-xl mr-1" v-if="discount.calc_type === 'flat'">{{ formatCurrency(discount.amount) }}</span>
                <span class="text-xl mr-1" v-else>{{ parseFloat((discount.percent * 100).toFixed(2)) }}%</span>
                <el-icon class="text-gray-400 cursor-pointer" size="16" @click="removeInvoiceDiscount(discount)">
                  <Close />
                </el-icon>
              </div>
            </template>
          </x-item>
        </el-col>
      </el-row>
    </div>
    <!-- Balance Section -->
    <div v-if="Number(currentInvoice?.minus_credit || 0) > 0">
      <div class="text-sm">{{ t('payment.balance') }}</div>
      <div>
        <el-row :gutter="12">
          <el-col :span="12" class="mb-3">
            <x-item border class="w-full shrink-0 bg-white">
              <template #start>
                <div class="w-[60px] h-[40px] flex items-center justify-center text-primary rounded-lg">
                  <el-icon size="24">
                    <Pay />
                  </el-icon>
                </div>
              </template>
              {{ t('payment.balance') }}
              <template #end>
                <div class="flex flex-wrap w-[100px] items-center justify-end">
                  <span class="text-xl mr-1">{{ formatCurrency(Number(currentInvoice?.minus_credit || 0)) }}</span>
                  <el-icon class="text-gray-400 cursor-pointer" size="16" @click="removeBalanceUsage">
                    <Close />
                  </el-icon>
                </div>
              </template>
            </x-item>
          </el-col>
        </el-row>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
</style>
